import { clamp01 } from '@/core/math/scalar';
import { BatteryDef, PowerPlantDef } from './ShipDef';

export interface PowerRequest {
  id: string;
  /** Watts requested. */
  watts: number;
  /** Higher priority consumers are served first during a brownout. */
  priority: number;
}

export interface PowerReport {
  generation: number;
  demand: number;
  delivered: number;
  batteryCharge: number;
  batteryCapacity: number;
  /** Fraction of total demand satisfied, 0..1. */
  satisfaction: number;
  /** Per-consumer satisfaction, 0..1. */
  perConsumer: Map<string, number>;
  brownout: boolean;
}

/**
 * Electrical bus.
 *
 * Priority matters here rather than being decoration: when the field drive asks
 * for more power than the reactor can supply, life support and avionics are
 * served first and the drive is the one that browns out. That is why the field
 * drive's `limitReason` can read "Insufficient electrical power" mid-ascent
 * instead of the vessel silently losing lift.
 */
export class PowerSystem {
  /** Stored energy, joules. */
  charge: number;
  readonly capacity: number;
  private readonly maxDischarge: number;
  private readonly plants: PowerPlantDef[];
  /** Whether the solar arrays are deployed. */
  solarDeployed = true;
  /** Per-plant enable flags keyed by plant id. */
  private disabled = new Set<string>();

  private requests: PowerRequest[] = [];

  constructor(plants: PowerPlantDef[], batteries: BatteryDef[]) {
    this.plants = plants;
    this.capacity = batteries.reduce((s, b) => s + b.capacity, 0);
    this.maxDischarge = batteries.reduce((s, b) => s + b.maxDischarge, 0);
    this.charge = this.capacity;
  }

  setPlantEnabled(id: string, enabled: boolean): void {
    if (enabled) this.disabled.delete(id);
    else this.disabled.add(id);
  }

  isPlantEnabled(id: string): boolean {
    return !this.disabled.has(id);
  }

  /**
   * Instantaneous generation, watts.
   * `irradiance` is the local stellar flux in W/m^2 and only affects solar.
   */
  generation(irradiance: number, fuelAvailable: (fuelType: string) => boolean): number {
    let total = 0;
    for (const p of this.plants) {
      if (this.disabled.has(p.id)) continue;
      if (p.solar) {
        if (!this.solarDeployed) continue;
        total += (p.area ?? 0) * Math.max(0, irradiance) * 0.28;
      } else {
        if (p.fuelType && !fuelAvailable(p.fuelType)) continue;
        total += p.output;
      }
    }
    return total;
  }

  /** Fuel burn of the reactors this step, keyed by fuel type. */
  fuelBurn(dt: number): Map<string, number> {
    const out = new Map<string, number>();
    for (const p of this.plants) {
      if (this.disabled.has(p.id) || p.solar || !p.fuelType || p.fuelRate <= 0) continue;
      out.set(p.fuelType, (out.get(p.fuelType) ?? 0) + p.fuelRate * dt);
    }
    return out;
  }

  request(id: string, watts: number, priority = 0): void {
    if (watts <= 0) return;
    this.requests.push({ id, watts, priority });
  }

  /**
   * Resolves this step's requests against generation plus battery discharge.
   * Returns per-consumer satisfaction and updates the battery charge.
   */
  resolve(generationWatts: number, dt: number): PowerReport {
    const demand = this.requests.reduce((s, r) => s + r.watts, 0);
    const dischargeAvailable = Math.min(this.maxDischarge, dt > 0 ? this.charge / dt : 0);
    const available = generationWatts + dischargeAvailable;

    const perConsumer = new Map<string, number>();
    let delivered = 0;

    if (demand <= available || demand === 0) {
      for (const r of this.requests) perConsumer.set(r.id, 1);
      delivered = demand;
    } else {
      // Serve by descending priority, then proportionally within a tier.
      const sorted = [...this.requests].sort((a, b) => b.priority - a.priority);
      let remaining = available;
      for (const r of sorted) {
        const give = Math.min(r.watts, Math.max(0, remaining));
        perConsumer.set(r.id, r.watts > 0 ? give / r.watts : 0);
        remaining -= give;
        delivered += give;
      }
    }

    // Battery integrates the difference between generation and consumption.
    const net = generationWatts - delivered;
    this.charge = Math.min(this.capacity, Math.max(0, this.charge + net * dt));

    const report: PowerReport = {
      generation: generationWatts,
      demand,
      delivered,
      batteryCharge: this.charge,
      batteryCapacity: this.capacity,
      satisfaction: demand > 0 ? clamp01(delivered / demand) : 1,
      perConsumer,
      brownout: demand > available + 1e-6,
    };

    this.requests.length = 0;
    return report;
  }

  /** Watts the bus could supply right now without draining below `reserve`. */
  headroom(generationWatts: number, dt: number, reserveFraction = 0.05): number {
    const usable = Math.max(0, this.charge - this.capacity * reserveFraction);
    const discharge = Math.min(this.maxDischarge, dt > 0 ? usable / dt : 0);
    return generationWatts + discharge;
  }

  get chargeFraction(): number {
    return this.capacity > 0 ? clamp01(this.charge / this.capacity) : 0;
  }
}
